import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { axiosHelper, tokenHelper } from "../../../stores/helpers";
import { reactSelectStyling } from "../../../Components/Common/Forms/FormikInputHelper";
import { useOrderStore } from "../../../stores/Orders";
import MenuItemBarChart from "./Charts";

const localizedName = (row, language) => {
  if (language?.startsWith("ar") && row?.nameAr) return row.nameAr;
  return row?.name;
};

const MenuItemReport = () => {
  document.title = "Menu Item Report - Clickorder Admin";

  const { i18n } = useTranslation();
  const { statsLoading, getMenuItemReport } = useOrderStore((state) => state);

  const [sites, setSites] = useState([]);
  const [menus, setMenus] = useState([]);
  const [items, setItems] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [menuItemId, setMenuItemId] = useState(null);
  const [optionId, setOptionId] = useState(null);
  const [choiceId, setChoiceId] = useState(null);
  const [kitchenIds, setKitchenIds] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
    return start;
  });
  const [endDate, setEndDate] = useState(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end;
  });
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const accessToken = await tokenHelper.getToken();
        const headers = { Authorization: `Bearer ${accessToken}` };
        const [siteResponse, kitchenResponse] = await Promise.all([
          axiosHelper.get("/user/sites", { headers }),
          axiosHelper.get("/order/reporting/kitchens", { headers }),
        ]);
        setSites(siteResponse || []);
        setKitchens(kitchenResponse || []);
      } catch (err) {
        console.error("Error loading report filters:", err);
        setError("Failed to load sites");
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    if (!siteId) {
      setMenus([]);
      return;
    }

    const loadMenus = async () => {
      try {
        const accessToken = await tokenHelper.getToken();
        const response = await axiosHelper.get(
          `/menu/list/a?page=1&perPage=999&siteId=${siteId}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setMenus(response?.menus || []);
      } catch (err) {
        console.error("Error loading menus:", err);
        setMenus([]);
        setError("Failed to load menus");
      }
    };

    loadMenus();
  }, [siteId]);

  useEffect(() => {
    if (!menuId) {
      setItems([]);
      return;
    }

    const loadItems = async () => {
      setItemsLoading(true);
      try {
        const accessToken = await tokenHelper.getToken();
        const response = await axiosHelper.get(
          `/menu/portal/${menuId}?page=1&perPage=999`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setItems(response?.data?.menu_items || []);
      } catch (err) {
        console.error("Error loading menu items:", err);
        setItems([]);
        setError("Failed to load menu items");
      } finally {
        setItemsLoading(false);
      }
    };

    loadItems();
  }, [menuId]);

  const selectedItem = items.find((item) => item.item_id === menuItemId);
  const options = selectedItem?.options || [];
  const selectedOption = options.find((option) => option.option_id === optionId);
  const choices = selectedOption?.choices || [];

  const siteOptions = sites.map((site) => ({
    value: site.site_id,
    label: `${site.site_id} - ${site.name}`,
  }));
  const menuOptions = menus.map((menu) => ({
    value: menu.menu_id,
    label: `${menu.menu_id} - ${menu.name}`,
  }));
  const itemOptions = items.map((item) => ({
    value: item.item_id,
    label: item.title,
  }));
  const optionOptions = options.map((option) => ({
    value: option.option_id,
    label: option.name,
  }));
  const choiceOptions = choices.map((choice) => ({
    value: choice.choice_id,
    label: choice.name,
  }));
  const kitchenOptions = kitchens
    .filter((kitchen) => !siteId || kitchen.siteId === siteId)
    .map((kitchen) => ({
      value: kitchen.kitchenId,
      label: localizedName(kitchen, i18n.language),
    }));

  const clearReport = () => setReport(null);

  const handleDateChange = (dates) => {
    if (dates?.length === 2) {
      setStartDate(dates[0]);
      setEndDate(dates[1]);
      setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      clearReport();
    }
  };

  const getReport = async () => {
    setError(null);
    if (!siteId) {
      setError("Select a site");
      return;
    }
    if (!menuItemId) {
      setError("Select a menu item");
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a date range");
      return;
    }

    const response = await getMenuItemReport({
      menuItemId,
      startDate,
      endDate,
      timezone,
      choiceId,
      kitchenIds,
      siteId,
    });

    if (!response) {
      setReport(null);
      setError("Failed to generate the report. Try again.");
      return;
    }

    setReport(response);
  };

  const isEmpty =
    report &&
    report.deliveredCount === 0 &&
    (report.spaces || []).length === 0 &&
    (report.kitchens || []).length === 0;

  const currency = useMemo(() => {
    if (!report) return "";
    if (i18n.language?.startsWith("ar") && report.currencyAr) return report.currencyAr;
    return report.currency || "";
  }, [report, i18n.language]);

  const formatMoney = (amount) => {
    const value = Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currency ? `${currency} ${value}` : value;
  };

  const itemTitle =
    i18n.language?.startsWith("ar") && report?.titleAr ? report.titleAr : report?.title;
  const kitchenLabels = (report?.kitchens || []).map((kitchen) =>
    localizedName(kitchen, i18n.language)
  );

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col>
          <h4 className="fs-16 mb-1">Menu item report</h4>
          <p className="text-muted mb-0">Statistics for one item from your menus.</p>
        </Col>
      </Row>

      <Card>
        <CardBody>
          <Row className="g-3 align-items-end">
            <Col md={6} xl={3}>
              <label className="form-label">Site</label>
              <Select
                {...reactSelectStyling}
                options={siteOptions}
                placeholder="Select a site"
                isSearchable
                isClearable
                value={siteOptions.find((option) => option.value === siteId) || null}
                onChange={(option) => {
                  setSiteId(option?.value ?? null);
                  setMenuId(null);
                  setMenuItemId(null);
                  setOptionId(null);
                  setChoiceId(null);
                  setKitchenIds([]);
                  clearReport();
                }}
              />
            </Col>
            <Col md={6} xl={3}>
              <label className="form-label">Menu</label>
              <Select
                {...reactSelectStyling}
                options={menuOptions}
                placeholder="Select a menu"
                isSearchable
                isClearable
                isDisabled={!siteId}
                value={menuOptions.find((option) => option.value === menuId) || null}
                onChange={(option) => {
                  setMenuId(option?.value ?? null);
                  setMenuItemId(null);
                  setOptionId(null);
                  setChoiceId(null);
                  clearReport();
                }}
              />
            </Col>
            <Col md={6} xl={3}>
              <label className="form-label">Item</label>
              <Select
                {...reactSelectStyling}
                options={itemOptions}
                placeholder={itemsLoading ? "Loading items" : "Select an item"}
                isSearchable
                isClearable
                isDisabled={!menuId || itemsLoading}
                isLoading={itemsLoading}
                value={itemOptions.find((option) => option.value === menuItemId) || null}
                onChange={(option) => {
                  setMenuItemId(option?.value ?? null);
                  setOptionId(null);
                  setChoiceId(null);
                  clearReport();
                }}
              />
            </Col>
            <Col md={6} xl={3}>
              <label className="form-label">Option</label>
              <Select
                {...reactSelectStyling}
                options={optionOptions}
                placeholder="Any option"
                isSearchable
                isClearable
                isDisabled={!selectedItem || optionOptions.length === 0}
                value={optionOptions.find((option) => option.value === optionId) || null}
                onChange={(option) => {
                  setOptionId(option?.value ?? null);
                  setChoiceId(null);
                  clearReport();
                }}
              />
            </Col>
            <Col md={6} xl={3}>
              <label className="form-label">Choice</label>
              <Select
                {...reactSelectStyling}
                options={choiceOptions}
                placeholder="Any choice"
                isSearchable
                isClearable
                isDisabled={!optionId}
                value={choiceOptions.find((option) => option.value === choiceId) || null}
                onChange={(option) => {
                  setChoiceId(option?.value ?? null);
                  clearReport();
                }}
              />
            </Col>
            <Col md={6} xl={4}>
              <label className="form-label">Kitchens</label>
              <Select
                {...reactSelectStyling}
                options={kitchenOptions}
                placeholder="All kitchens"
                isSearchable
                isClearable
                isMulti
                value={kitchenOptions.filter((option) => kitchenIds.includes(option.value))}
                onChange={(selected) => {
                  setKitchenIds(selected ? selected.map((option) => option.value) : []);
                  clearReport();
                }}
              />
            </Col>
            <Col md={6} xl={4}>
              <label className="form-label">Date range</label>
              <Flatpickr
                options={{
                  mode: "range",
                  enableTime: true,
                  dateFormat: "Y-m-d H:i",
                  defaultDate: [startDate, endDate],
                }}
                className="form-control bg-white"
                value={[startDate, endDate]}
                onChange={handleDateChange}
              />
            </Col>
            <Col md={12} xl={4}>
              <Button
                style={{ background: "#ce9558", borderColor: "#ce9558" }}
                onClick={getReport}
                disabled={statsLoading}
              >
                Generate report
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {error && (
        <Alert color="danger">
          <i className="ri-error-warning-line me-2"></i>
          {error}
        </Alert>
      )}

      {statsLoading && (
        <div className="text-center py-4">
          <Spinner color="primary" />
          <span className="ms-2">Loading report data...</span>
        </div>
      )}

      {!statsLoading && report && isEmpty && (
        <Alert color="info">This item had no matching orders in this range.</Alert>
      )}

      {!statsLoading && report && !isEmpty && (
        <>
          <div className="d-flex flex-wrap align-items-baseline gap-2 mb-3">
            <h5 className="mb-0">{itemTitle}</h5>
            <span className="text-muted">
              {moment(startDate).format("DD MMM YYYY")} – {moment(endDate).format("DD MMM YYYY")}
            </span>
          </div>

          <Row className="mb-3">
            <Col md={4}>
              <Card>
                <CardBody>
                  <p className="text-muted mb-1">Delivered count</p>
                  <h4 className="mb-0">{report.deliveredCount}</h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <p className="text-muted mb-1">Estimated revenue</p>
                  <h4 className="mb-0">{formatMoney(report.estimatedRevenue)}</h4>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card>
                <CardBody>
                  <p className="text-muted mb-1">Spaces that ordered it</p>
                  <h4 className="mb-0">
                    {new Set((report.spaces || []).map((space) => space.spaceId)).size}
                  </h4>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} className="mb-3">
              <Card className="mb-0">
                <CardBody>
                  <MenuItemBarChart
                    title="Times ordered per day"
                    seriesName="Times ordered"
                    showValues
                    categories={(report.dailyOrders || []).map((day) =>
                      moment(day.date, "YYYY-MM-DD").format("DD MMM")
                    )}
                    data={(report.dailyOrders || []).map((day) => Number(day.count) || 0)}
                    emptyLabel="No orders in this range."
                  />
                </CardBody>
              </Card>
            </Col>
            <Col xs={12}>
              <Card className="mb-0">
                <CardBody>
                  <MenuItemBarChart
                    title="Kitchens that delivered it"
                    seriesName="Delivered"
                    categories={kitchenLabels}
                    data={(report.kitchens || []).map((kitchen) => kitchen.deliveredCount)}
                    emptyLabel="No kitchen delivered this item in this range."
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          <div className="menu-item-space-grid">
            {Array.from({ length: 4 }, (_, columnIndex) => {
              const columnSpaces = (report.spaces || []).filter(
                (_, index) => index % 4 === columnIndex
              );
              if (columnSpaces.length === 0) return <div key={columnIndex} />;
              return (
                <table key={columnIndex} className="table table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Space</th>
                      <th className="text-end">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columnSpaces.map((space) => (
                      <tr key={space.spaceId}>
                        <td>{localizedName(space, i18n.language)}</td>
                        <td className="text-end">{space.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })}
          </div>
          <style>{`
            .menu-item-space-grid {
              display: grid;
              grid-template-columns: repeat(4, minmax(0, 1fr));
              gap: 16px;
              align-items: start;
            }
            .menu-item-space-grid table {
              background: var(--vz-card-bg, #fff);
              border: 1px solid var(--vz-border-color, #e9ebec);
              border-radius: 6px;
              overflow: hidden;
            }
            .menu-item-space-grid th,
            .menu-item-space-grid td {
              padding: 8px 12px;
            }
            @media (max-width: 991px) {
              .menu-item-space-grid {
                grid-template-columns: minmax(0, 1fr);
              }
            }
          `}</style>
        </>
      )}
    </Container>
  );
};

export default MenuItemReport;
